import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import Icon from "@/components/Icon";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { getSingleDateShortcuts } from "@/lib/dateShortcuts";
import {
  DATE_ONLY_PATTERN,
  dateOnlyToJsDate,
  EXPLICIT_TIMEZONE_PATTERN,
  formatDisplayDate,
  formatPrettyDate,
  isDateOnlyInRange,
  parseInputDate
} from "@/lib/dateHelpers";
import "../DateSelect/DatePicker.css";
import "./DateInput.css";

export interface DateInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  error?: boolean;
  errorMessage?: string;
  supportingText?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  allowClear?: boolean;
  hideClearButton?: boolean;
  showCalendar?: boolean;
  useModal?: boolean;
  showShortcuts?: boolean;
  popoverDirection?: "up" | "down";
}

function toPickerValue(iso: string | null): DateValueType {
  if (!iso) return { startDate: null, endDate: null };
  return { startDate: iso, endDate: iso };
}

function fromPickerValue(dateValue: DateValueType): string | null {
  const start = dateValue?.startDate;
  if (!start) return null;
  if (typeof start === "string") {
    return DATE_ONLY_PATTERN.test(start) ? start : parseInputDate(start);
  }
  const date = DateTime.fromJSDate(start);
  return date.isValid ? date.toFormat("yyyy-MM-dd") : null;
}

function maskDateDigits(raw: string, previousDisplay = ""): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const isDeleting = raw.length < previousDisplay.length;

  if (digits.length === 0) return "";
  if (digits.length <= 2) {
    if (digits.length === 2 && !isDeleting) return `${digits}/`;
    return digits;
  }
  if (digits.length <= 4) {
    const monthDay = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    if (digits.length === 4 && !isDeleting) return `${monthDay}/`;
    return monthDay;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function looksLikeDigitMask(value: string): boolean {
  return /^[\d/]*$/.test(value);
}

const COMPLETE_DISPLAY_DATE = /^\d{2}\/\d{2}\/\d{4}$/;

function shouldLiveCommit(next: string, previous: string): boolean {
  const trimmed = next.trim();
  if (DATE_ONLY_PATTERN.test(trimmed)) return true;
  if (trimmed.includes("T") || EXPLICIT_TIMEZONE_PATTERN.test(trimmed)) return true;
  if (/[A-Za-z]/.test(trimmed)) return true;
  if (COMPLETE_DISPLAY_DATE.test(trimmed)) return true;
  return next.length - previous.length > 1 && parseInputDate(trimmed) !== null;
}

function isAppendOrEndTrimDigitEntry(next: string, previous: string): boolean {
  if (!looksLikeDigitMask(next)) return false;
  if (!previous) return true;
  const nextDigits = next.replace(/\D/g, "");
  const prevDigits = previous.replace(/\D/g, "");
  if (nextDigits === prevDigits) return true;
  return nextDigits.startsWith(prevDigits) || prevDigits.startsWith(nextDigits);
}

function commitIfValid(
  candidate: string | null,
  minDate: Date | undefined,
  maxDate: Date | undefined,
  onChange: (value: string | null) => void
): string | null {
  if (!candidate) {
    onChange(null);
    return null;
  }
  if (!isDateOnlyInRange(candidate, minDate, maxDate)) return null;
  onChange(candidate);
  return candidate;
}

export default function DateInput({
  value,
  onChange,
  onFocus,
  onBlur,
  label = "",
  placeholder = "MM/DD/YYYY",
  minDate,
  maxDate,
  error,
  errorMessage,
  supportingText,
  disabled = false,
  className,
  required = false,
  allowClear = true,
  hideClearButton = false,
  showCalendar = true,
  useModal = false,
  showShortcuts = false,
  popoverDirection = "down"
}: DateInputProps) {
  const [displayValue, setDisplayValue] = useState(() => formatPrettyDate(value));
  const [isFocused, setIsFocused] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarHostRef = useRef<HTMLDivElement>(null);
  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const selectAllOnMouseUpRef = useRef(false);
  const placeCaretAtEndRef = useRef(false);
  const inputId = useId();
  const descriptionId = `${inputId}-description`;
  const { isCompactLayout } = useIsCompactLayout();
  const shouldUseModal = useModal || isCompactLayout;
  const shortcuts = showShortcuts ? getSingleDateShortcuts() : undefined;
  const showClear = allowClear && !hideClearButton && !!value && !disabled;
  const descriptionText = error ? errorMessage : supportingText;

  useEffect(() => {
    if (isFocused) {
      setDisplayValue(formatDisplayDate(value));
      return;
    }
    setDisplayValue(formatPrettyDate(value));
  }, [value, isFocused]);

  const selectInputText = () => {
    inputRef.current?.select();
  };

  useLayoutEffect(() => {
    if (!isFocused || disabled) return;
    selectInputText();
  }, [isFocused, disabled]);

  useLayoutEffect(() => {
    if (!placeCaretAtEndRef.current) return;
    placeCaretAtEndRef.current = false;
    const el = inputRef.current;
    if (!el) return;
    const pos = el.value.length;
    el.setSelectionRange(pos, pos);
  }, [displayValue]);

  const closeCalendar = useCallback(() => setIsCalendarOpen(false), []);
  const pickerInteractedRef = useRef(false);

  useLayoutEffect(() => {
    if (!isCalendarOpen || !calendarHostRef.current) return;
    const calendar = calendarHostRef.current.querySelector("div.absolute.transition-all");
    calendar?.classList.remove("hidden", "opacity-0", "translate-y-4");
    calendar?.classList.add("block", "opacity-1", "translate-y-0");
    calendarHostRef.current
      .querySelectorAll(".calendar-inline-container > input, .calendar-inline-container > button")
      .forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        node.setAttribute("aria-hidden", "true");
        node.setAttribute("role", "presentation");
        node.tabIndex = -1;
      });
    pickerInteractedRef.current = false;
    const host = calendarHostRef.current;
    const markInteracted = () => {
      pickerInteractedRef.current = true;
    };
    host.addEventListener("pointerdown", markInteracted);
    return () => {
      host.removeEventListener("pointerdown", markInteracted);
      pickerInteractedRef.current = false;
    };
  }, [isCalendarOpen]);

  useEffect(() => {
    if (!isCalendarOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (calendarHostRef.current?.contains(target)) return;
      if (calendarButtonRef.current?.contains(target)) return;
      if (inputRef.current?.contains(target)) return;
      closeCalendar();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeCalendar();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isCalendarOpen, closeCalendar]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (!next.trim()) {
      setDisplayValue("");
      if (allowClear) onChange(null);
      return;
    }

    if (shouldLiveCommit(next, displayValue)) {
      const parsed = parseInputDate(next);
      if (parsed) {
        const committed = commitIfValid(parsed, minDate, maxDate, onChange);
        if (committed) {
          setDisplayValue(isFocused ? formatDisplayDate(committed) : formatPrettyDate(committed));
          return;
        }
      }
    }

    if (isAppendOrEndTrimDigitEntry(next, displayValue)) {
      const masked = maskDateDigits(next, displayValue);
      placeCaretAtEndRef.current = masked.length > next.length && masked.endsWith("/");
      setDisplayValue(masked);
      return;
    }

    setDisplayValue(next);
  };

  const handleFocus = () => {
    selectAllOnMouseUpRef.current = true;
    setIsFocused(true);
    onFocus?.();
    if (value) setDisplayValue(formatDisplayDate(value));
  };

  const handleMouseUp = () => {
    if (!selectAllOnMouseUpRef.current) return;
    selectAllOnMouseUpRef.current = false;
    selectInputText();
  };

  const handleBlur = () => {
    selectAllOnMouseUpRef.current = false;
    const parsed = parseInputDate(displayValue);
    const committed = parsed ? commitIfValid(parsed, minDate, maxDate, onChange) : null;
    if (committed) {
      setDisplayValue(formatPrettyDate(committed));
    } else if (!displayValue.trim() && allowClear) {
      onChange(null);
      setDisplayValue("");
    } else {
      setDisplayValue(formatPrettyDate(value));
    }
    setIsFocused(false);
    onBlur?.();
  };

  const handleClear = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange(null);
    setDisplayValue("");
    closeCalendar();
    inputRef.current?.focus();
  };

  const handleCalendarButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    setIsCalendarOpen((open) => !open);
  };

  const handleCalendarChange = (nextValue: DateValueType) => {
    if (!pickerInteractedRef.current) return;
    const iso = fromPickerValue(nextValue);
    if (iso) commitIfValid(iso, minDate, maxDate, onChange);
    closeCalendar();
  };

  return (
    <div
      data-testid="date-input"
      className={classNames("date-input relative w-full", className, {
        "use-modal": shouldUseModal && showCalendar && isCalendarOpen
      })}
    >
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-invalid={error || undefined}
        aria-describedby={error || supportingText ? descriptionId : undefined}
        onMouseUp={handleMouseUp}
        className={classNames(
          "w-full h-[56px] pl-4 border text-sys-brand-on-surface placeholder-sys-brand-outline border-sys-brand-outline outline-sys-brand-outline bg-sys-brand-surface rounded-[4px]",
          {
            "pr-24": showClear && showCalendar,
            "pr-12": (showClear && !showCalendar) || (!showClear && showCalendar),
            "border-sys-brand-primary outline-sys-brand-primary": isFocused && !error,
            "!border-sys-brand-error !outline-sys-brand-error text-sys-brand-error": error,
            "!bg-sys-brand-disabled text-sys-brand-disabled outline-sys-brand-disabled opacity-50": disabled
          }
        )}
      />
      {label && (
        <label
          htmlFor={inputId}
          className={classNames(
            "absolute px-1 -top-[8px] left-[12px] bg-sys-brand-surface text-body-small text-sys-brand-on-surface",
            {
              "text-sys-brand-primary": isFocused,
              "!text-sys-brand-error": error,
              "text-opacity-50": disabled
            }
          )}
        >
          {label}
        </label>
      )}
      {(showClear || showCalendar) && (
        <div className="absolute right-3 top-[28px] -translate-y-1/2 flex items-center gap-2">
          {showClear && (
            <button
              type="button"
              onClick={handleClear}
              className="h-5 w-5 flex items-center justify-center cursor-pointer"
              aria-label="Clear date"
              data-testid="date-input-clear"
              tabIndex={-1}
            >
              <Icon name="close" size={20} />
            </button>
          )}
          {showCalendar && (
            <button
              ref={calendarButtonRef}
              type="button"
              onClick={handleCalendarButtonClick}
              disabled={disabled}
              className={classNames("h-5 w-5 flex items-center justify-center cursor-pointer", {
                "opacity-50 cursor-not-allowed": disabled
              })}
              aria-label="Open calendar"
              aria-expanded={isCalendarOpen}
              data-testid="date-input-calendar"
              tabIndex={-1}
            >
              <Icon name="calendar_today" size={20} />
            </button>
          )}
        </div>
      )}
      {showCalendar && isCalendarOpen && (
        <div
          ref={calendarHostRef}
          className={classNames("calendar-host", !shouldUseModal && "absolute left-0 z-[100] mt-1")}
          style={popoverDirection === "up" ? { bottom: "100%", top: "auto", marginBottom: "0.25rem" } : undefined}
        >
          <Datepicker
            value={toPickerValue(value)}
            onChange={handleCalendarChange}
            asSingle
            useRange={false}
            showFooter={false}
            showShortcuts={showShortcuts}
            configs={{ shortcuts }}
            minDate={minDate}
            maxDate={maxDate}
            popoverDirection={popoverDirection}
            startFrom={dateOnlyToJsDate(value)}
            toggleClassName="hidden"
            inputClassName="hidden"
            containerClassName="calendar-inline-container"
          />
        </div>
      )}
      {(error || supportingText) && (
        <div
          id={descriptionId}
          className={classNames(
            "text-body-small ml-4 mt-1 whitespace-pre-line",
            error ? "text-sys-brand-error" : "text-sys-brand-on-surface",
            !error && disabled && "opacity-50"
          )}
        >
          {descriptionText}
        </div>
      )}
    </div>
  );
}
